import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Phone, MessageCircle, ArrowRight, Clock, User, BookOpen } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_LABELS = {
  psychiatry: "Psychiatric Disorders",
  deaddiction: "Drug Addiction",
  sexual_health: "Sexual Health",
  overlap: "The Overlap Zone",
};

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/articles/${slug}`)
      .then(res => { setArticle(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!article) return <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4"><p className="text-muted-foreground">Article not found.</p><Link to="/resources"><Button variant="outline">Back to Resources</Button></Link></div>;

  return (
    <div>
      {/* Header */}
      <section className="py-12 sm:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-resources">
            <ArrowLeft className="w-4 h-4" /> Back to Resources
          </Link>
          <span className="text-xs text-accent font-medium uppercase tracking-wider block mb-3">
            {PILLAR_LABELS[article.pillar] || article.pillar}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{article.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{article.read_time}</span>
            {article.published_date && <span>{article.published_date}</span>}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            <article className="lg:col-span-3 space-y-6" data-testid="article-content">
              {article.content?.map((block, i) => {
                if (block.type === "heading") {
                  return <h2 key={i} className="font-heading text-xl sm:text-2xl font-semibold mt-8 mb-3">{block.body}</h2>;
                }
                if (block.type === "cta") {
                  return (
                    <Card key={i} className="border-primary/20 bg-primary/5 mt-8">
                      <CardContent className="p-6">
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{block.body}</p>
                        <div className="flex flex-wrap gap-3">
                          <a href="tel:+919915096633">
                            <Button size="sm" className="gap-2 rounded-full">
                              <Phone className="w-4 h-4" /> Call Now
                            </Button>
                          </a>
                          <a href="https://wa.me/919815333660" target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="gap-2 rounded-full">
                              <MessageCircle className="w-4 h-4" /> WhatsApp
                            </Button>
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }
                return <p key={i} className="text-base text-muted-foreground leading-relaxed">{block.body}</p>;
              })}
            </article>

            {/* Sidebar */}
            <aside className="space-y-6 hidden lg:block">
              <Card className="border sticky top-24">
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-heading font-semibold">Consult Dr. Singal</h3>
                  <p className="text-sm text-muted-foreground">28+ years experience in psychiatry, de-addiction, and sexual health.</p>
                  <a href="tel:+919915096633" className="block">
                    <Button className="w-full gap-2 rounded-full" size="sm">
                      <Phone className="w-4 h-4" /> +91-99150 96633
                    </Button>
                  </a>
                  <Link to="/contact" className="block">
                    <Button variant="outline" className="w-full gap-2 rounded-full" size="sm">
                      Book Appointment <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
